import React, { useState, useEffect, useRef } from 'react';
import { useLayeredHistory } from '@/hooks/use-layered-history';
import { compressImageToJpeg } from '@/lib/compress-image';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

import {
  Handshake,
  ArrowUpRight,
  ArrowDownLeft,
  ChevronRight,
  Info,
  CheckCircle2,
  Calendar,
  Send,
  Loader2,
  Clock,
  Mail,
  Camera,
  X,
  Check,
  Plus,
  ReceiptText,
  Search,
  ZoomIn,
  Copy,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { cn, formatAmount } from '@/lib/utils';
import { useToast } from '../hooks/use-toast';
import { Expense, User, Settlement, ExpenseParticipant } from '../types';
import { ImageLightbox } from '@/components/ui/ImageLightbox';

interface DebtDetail {
  expenseId: string;
  participantId: string;
  title: string;
  amount: number;
  date: string;
}

interface UserDebt {
  userId: string;
  nickname: string;
  totalAmount: number;
  details: DebtDetail[];
}

export default function Settlements({ store }: { store: any }) {
  const { expenses, settlements, currentUser, settle, users, sendReminder, refresh } = store;
  const { showToast } = useToast();
  const settlementPageSize = 10;
  const mobileBreakpoint = 768;
  const mobileLoadMoreMargin = 160;
  const [selectedDebt, setSelectedDebt] = useState<{
    user: UserDebt;
    type: 'incoming' | 'outgoing';
  } | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [receiptFile, setReceiptFile] = useState<File | null>(0 as any); // Fix type issue
  const [receiptPreview, setReceiptPreview] = useState<string>('');
  const [isSettling, setIsSettling] = useState(false);
  const [isReminding, setIsReminding] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState<Settlement | null>(null);
  const [selectedExpenseDetail, setSelectedExpenseDetail] = useState<Expense | null>(null);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [settlementPage, setSettlementPage] = useState(1);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(settlementPageSize);
  const mobileLoadMoreRef = useRef<HTMLDivElement | null>(null);

  // Auto-open settlement from URL on initial load (e.g. /settlements/:id)
  // 在 component 初始化時就抓好 URL 裡的 ID，避免後面 URL 同步 effect 覆蓋掉
  const initialSettlementIdRef = useRef<string | null>(
    typeof window !== 'undefined'
      ? (window.location.pathname.match(/^\/settlements\/(.+)/)?.[1] ?? null)
      : null
  );
  const didAutoOpen = useRef(false);
  // 追蹤是否因使用者點擊而 pushState（非深層連結）
  const didPushHistoryRef = useRef(false);

  const { pushLayer, popLayer, historyLayerRef } = useLayeredHistory((layer) => {
    if (layer === 'zoomedImage') {
      setZoomedImage(null);
    } else if (layer === 'debt') {
      setSelectedDebt(null);
    } else {
      // settlement layer or deep-link
      didPushHistoryRef.current = false;
      setSelectedSettlement(null);
    }
  });

  useEffect(() => {
    if (didAutoOpen.current || !settlements.length) return;
    const targetId = initialSettlementIdRef.current;
    if (!targetId) { didAutoOpen.current = true; return; }
    const found = settlements.find((s: Settlement) => String(s.id) === targetId);
    if (found) {
      setSelectedSettlement(found);
      didAutoOpen.current = true;
    }
  }, [settlements]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < mobileBreakpoint);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const mySettlements = settlements.filter(
    (s: Settlement) => s.fromUserId === currentUser?.id || s.toUserId === currentUser?.id,
  );
  const totalSettlementPages = Math.max(1, Math.ceil(mySettlements.length / settlementPageSize));
  const pagedSettlements = mySettlements.slice(
    (settlementPage - 1) * settlementPageSize,
    settlementPage * settlementPageSize,
  );
  const mobileSettlements = mySettlements.slice(0, mobileVisibleCount);

  useEffect(() => {
    setSettlementPage(1);
    setMobileVisibleCount(settlementPageSize);
  }, [currentUser?.id, settlements.length]);

  useEffect(() => {
    if (!isMobile) return;
    if (mobileVisibleCount >= mySettlements.length) return;
    const target = mobileLoadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        setMobileVisibleCount((prev) => Math.min(prev + settlementPageSize, mySettlements.length));
      },
      { rootMargin: `0px 0px ${mobileLoadMoreMargin}px 0px` },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [isMobile, mobileVisibleCount, mySettlements.length]);

  const handleShare = () => {
    if (typeof window === 'undefined') return;
    navigator.clipboard.writeText(window.location.href).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Dialog 開啟時 pushState 新增歷史，讓上一頁可以關閉 dialog
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (selectedSettlement) {
      const targetPath = `/settlements/${selectedSettlement.id}`;
      if (window.location.pathname !== targetPath) {
        window.history.pushState(null, '', targetPath);
        historyLayerRef.current = ['settlement'];
        didPushHistoryRef.current = true;
      }
    }
  }, [selectedSettlement]);

  const closeSettlementDialog = () => {
    if (typeof window !== 'undefined') {
      if (didPushHistoryRef.current) {
        didPushHistoryRef.current = false;
        historyLayerRef.current = [];
        window.history.back();
      } else if (window.location.pathname.startsWith('/settlements/')) {
        window.history.replaceState(null, '', '/settlements');
      }
    }
    setSelectedSettlement(null);
  };

  const handleSendReminder = async () => {
    if (!selectedDebt || selectedDebt.type !== 'incoming') return;
    setIsReminding(true);
    try {
      // Find one relevant expense title for the subject
      const expenseTitle = selectedDebt.user.details[0]?.title || '待結清項目';
      await sendReminder(selectedDebt.user.userId, selectedDebt.user.totalAmount, expenseTitle);
      showToast('提醒信件已寄出！', 'success');
    } catch (err) {
      showToast('寄送失敗，請檢查 Email 設定。', 'error');
    } finally {
      setIsReminding(false);
    }
  };

  // Calculate who owes me (Incoming)
  const othersOweMe: Record<string, UserDebt> = {};
  expenses.forEach((exp: Expense) => {
    if (exp.payerId === currentUser?.id) {
      exp.participants.forEach(p => {
        if (p.userId !== currentUser.id && !p.isPaid) {
          const user = users.find((u: User) => u.id === p.userId);
          if (!othersOweMe[p.userId]) {
            othersOweMe[p.userId] = {
              userId: p.userId,
              nickname: user?.nickname || '未知',
              totalAmount: 0,
              details: []
            };
          }
          othersOweMe[p.userId].totalAmount += p.amountDue;
          othersOweMe[p.userId].details.push({
            expenseId: exp.id,
            participantId: p.id,
            title: exp.title,
            amount: p.amountDue,
            date: exp.expenseDate
          });
        }
      });
    }
  });

  // Calculate who I owe (Outgoing)
  const iOweOthers: Record<string, UserDebt> = {};
  expenses.forEach((exp: Expense) => {
    if (exp.payerId !== currentUser?.id) {
      const myParticipation = exp.participants.find(p => p.userId === currentUser?.id);
      if (myParticipation && !myParticipation.isPaid) {
        const payerUser = users.find((u: User) => u.id === exp.payerId);
        if (!iOweOthers[exp.payerId]) {
          iOweOthers[exp.payerId] = {
            userId: exp.payerId,
            nickname: payerUser?.nickname || '未知',
            totalAmount: 0,
            details: []
          };
        }
        iOweOthers[exp.payerId].totalAmount += myParticipation.amountDue;
        iOweOthers[exp.payerId].details.push({
          expenseId: exp.id,
          participantId: myParticipation.id,
          title: exp.title,
          amount: myParticipation.amountDue,
          date: exp.expenseDate
        });
      }
    }
  });

  const handleSettle = async () => {
    if (!selectedDebt || !currentUser) return;
    setIsSettling(true);
    try {
      const fromUserId = selectedDebt.type === 'outgoing' ? currentUser.id : selectedDebt.user.userId;
      const toUserId = selectedDebt.type === 'outgoing' ? selectedDebt.user.userId : currentUser.id;

      const selectedParticipantIds = selectedItemIds.length > 0
        ? selectedDebt.user.details.filter((d: DebtDetail) => selectedItemIds.includes(d.participantId)).map((d: DebtDetail) => d.participantId)
        : selectedDebt.user.details.map((d: DebtDetail) => d.participantId);

      const settlementAmount = selectedDebt.user.details
        .filter((d: DebtDetail) => selectedParticipantIds.includes(d.participantId))
        .reduce((sum: number, d: DebtDetail) => sum + d.amount, 0);

      const formData = new FormData();
      formData.append('fromUserId', fromUserId);
      formData.append('toUserId', toUserId);
      formData.append('totalAmount', settlementAmount.toString());
      formData.append('participantIds', JSON.stringify(selectedParticipantIds));
      formData.append('expenseIds', JSON.stringify(Array.from(new Set(selectedDebt.user.details.filter((d: DebtDetail) => selectedParticipantIds.includes(d.participantId)).map((d: DebtDetail) => d.expenseId)))));
      if (receiptFile instanceof File) {
        formData.append('receipt', receiptFile);
      }

      await settle(formData);
      // Force refresh after settlement
      setTimeout(() => refresh(), 500);
      setSelectedDebt(null);
      setSelectedItemIds([]);
      setReceiptFile(null as any);
      setReceiptPreview('');
      showToast('結算完成', 'success');
    } catch (err) {
      console.error(err);
      showToast('結算失敗，請重試', 'error');
    } finally {
      setIsSettling(false);
    }
  };

  const handleItemToggle = (participantId: string) => {
    setSelectedItemIds((prev: string[]) =>
      prev.includes(participantId) ? prev.filter((id: string) => id !== participantId) : [...prev, participantId]
    );
  };

  const settlementLinkedExpenses: { expense: Expense; linkedParticipants: ExpenseParticipant[] }[] = selectedSettlement
    ? expenses
        .filter((expense: Expense) =>
          expense.participants.some((p: any) => p.settlementId === selectedSettlement.id)
        )
        .map((expense: Expense) => {
          const linkedParticipants = expense.participants.filter((p: any) => p.settlementId === selectedSettlement.id);
          return { expense, linkedParticipants };
        })
    : [];

  const handleReceiptUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImageToJpeg(file, 409_600);
      setReceiptFile(compressed);
      const reader = new FileReader();
      reader.onloadend = () => setReceiptPreview(reader.result as string);
      reader.readAsDataURL(compressed);
    } catch (err) {
      console.error('圖片壓縮失敗，改用原檔：', err);
      setReceiptFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setReceiptPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
        <div>
          <h1 className="text-5xl font-black tracking-tight text-primary mb-2">結算中心</h1>
          <p className="text-muted-foreground max-w-lg">
            清晰透明的債務管理系統，協助您紀錄每一筆回款與支付進度。
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* I Owe Others (Outgoing) */}
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-black text-destructive flex items-center gap-3 drop-shadow-[0_0_12px_rgba(255,59,48,0.35)]">
              <ArrowDownLeft size={24} />
              我欠別人的
            </h3>
            <Badge variant="outline" className="border-destructive/40 text-destructive bg-destructive/10 font-black uppercase tracking-widest text-[10px]">
              OUTGOING
            </Badge>
          </div>

          <div className="space-y-4">
            {Object.values(iOweOthers).length > 0 ? (
              Object.values(iOweOthers).map((debt) => (
                <Card
                  key={debt.userId}
                  className="bg-muted/10 border-border/50 rounded-3xl p-6 group hover:border-destructive/30 transition-all cursor-pointer"
                  onClick={() => { setSelectedDebt({ user: debt, type: 'outgoing' }); pushLayer('debt'); }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Avatar className="w-12 h-12 rounded-2xl border-2 border-background overflow-hidden">
                        <AvatarImage src={users.find((u: User) => u.id === debt.userId)?.avatarUrl} className="object-cover" />
                        <AvatarFallback className="bg-destructive/10 text-destructive font-bold">
                          {debt.nickname[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="font-bold text-lg">{debt.nickname}</h4>
                        <p className="text-xs text-muted-foreground font-medium">{debt.details.length} 筆待結算</p>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-4">
                      <div className="text-2xl font-black text-destructive">${formatAmount(debt.totalAmount)}</div>
                      <ChevronRight size={20} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="p-12 text-center text-muted-foreground bg-muted/20 rounded-[2rem] border-2 border-dashed border-border/50 font-medium">
                目前沒有負債，太棒了！
              </div>
            )}
          </div>
        </section>

        {/* Others Owe Me (Incoming) */}
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-black text-secondary flex items-center gap-3 drop-shadow-[0_0_12px_rgba(52,199,89,0.35)]">
              <ArrowUpRight size={24} />
              別人欠我的
            </h3>
            <Badge variant="outline" className="border-secondary/40 text-secondary bg-secondary/10 font-black uppercase tracking-widest text-[10px]">
              INCOMING
            </Badge>
          </div>

          <div className="space-y-4">
            {Object.values(othersOweMe).length > 0 ? (
              Object.values(othersOweMe).map((debt) => (
                <Card
                  key={debt.userId}
                  className="bg-muted/10 border-border/50 rounded-3xl p-6 group hover:border-secondary/30 transition-all cursor-pointer"
                  onClick={() => { setSelectedDebt({ user: debt, type: 'incoming' }); pushLayer('debt'); }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Avatar className="w-12 h-12 rounded-2xl border-2 border-background overflow-hidden">
                        <AvatarImage src={users.find((u: User) => u.id === debt.userId)?.avatarUrl} className="object-cover" />
                        <AvatarFallback className="bg-secondary/10 text-secondary font-bold">
                          {debt.nickname[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="font-bold text-lg">{debt.nickname}</h4>
                        <p className="text-xs text-muted-foreground font-medium">{debt.details.length} 筆待入帳紀錄</p>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-4">
                      <div className="text-2xl font-black text-secondary">${formatAmount(debt.totalAmount)}</div>
                      <ChevronRight size={20} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="p-12 text-center text-muted-foreground bg-muted/20 rounded-[2rem] border-2 border-dashed border-border/50 font-medium">
                目前沒有人欠您錢。
              </div>
            )}
          </div>
        </section>
      </div>

      <section className="space-y-6">
        <h3 className="text-xl font-black text-primary flex items-center gap-3">
          <Clock size={24} />
          年度結算紀錄          </h3>
        <div className="bg-muted/10 rounded-[2rem] overflow-hidden p-2 border border-border/50">
          <div className="space-y-4 p-4">
            {mySettlements.length > 0 ? (
              (isMobile ? mobileSettlements : pagedSettlements).map((s: Settlement) => {
                const isMyPayment = s.fromUserId === currentUser?.id;
                const otherUser = users.find((u: User) => u.id === (isMyPayment ? s.toUserId : s.fromUserId));
                return (
                  <div
                    key={s.id}
                    onClick={() => setSelectedSettlement(s)}
                    className="flex items-center justify-between p-4 bg-background/50 rounded-xl hover:translate-x-1 transition-all group border border-border/20 cursor-pointer hover:border-primary/30"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isMyPayment ? 'bg-destructive/10 text-destructive' : 'bg-secondary/10 text-secondary'}`}>
                        {isMyPayment ? <Send size={20} /> : <CheckCircle2 size={20} />}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold">
                          {isMyPayment ? `支付給 ${otherUser?.nickname}` : `${otherUser?.nickname} 支付給你`}
                        </h4>
                        <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest">{new Date(s.createdAt).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-4">
                      <span className={`text-lg font-black ${isMyPayment ? 'text-destructive' : 'text-secondary'}`}>
                        {isMyPayment ? '-' : '+'}${formatAmount(s.totalAmount)}
                      </span>
                      <ChevronRight size={16} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center p-8 text-muted-foreground bg-muted/20 rounded-xl">尚無結算紀錄</div>
            )}

            {!isMobile && mySettlements.length > settlementPageSize && (
              <div className="flex items-center justify-center gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSettlementPage((prev) => Math.max(1, prev - 1))}
                  disabled={settlementPage === 1}
                >
                  上一頁
                </Button>
                {Array.from({ length: totalSettlementPages }, (_, i) => i + 1).map((pageNo) => (
                  <Button
                    key={pageNo}
                    type="button"
                    size="sm"
                    variant={pageNo === settlementPage ? 'default' : 'outline'}
                    onClick={() => setSettlementPage(pageNo)}
                  >
                    {pageNo}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSettlementPage((prev) => Math.min(totalSettlementPages, prev + 1))}
                  disabled={settlementPage === totalSettlementPages}
                >
                  下一頁
                </Button>
              </div>
            )}

            {isMobile && mySettlements.length > settlementPageSize && mobileVisibleCount < mySettlements.length && (
              <div ref={mobileLoadMoreRef} className="text-center py-2 text-xs text-muted-foreground font-medium">
                下拉繼續載入 10 筆...
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Debt Detail Modal */}
      <Dialog open={!!selectedDebt} onOpenChange={(open) => { if (!open) { setSelectedDebt(null); popLayer(); } }}>
        <DialogContent className="bg-muted border-border rounded-[2rem] p-4 sm:p-6 md:p-8 max-w-[min(95vw,1100px)] shadow-wash overflow-hidden flex flex-col max-h-[90vh]">
          <DialogHeader className="flex-shrink-0">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${selectedDebt?.type === 'outgoing' ? 'bg-destructive/10 text-destructive' : 'bg-secondary/10 text-secondary'}`}>
              <Handshake size={28} />
            </div>
            <DialogTitle className="text-2xl font-black text-primary">
              {selectedDebt?.user.nickname}
              <span className="ml-3 text-sm font-bold opacity-60">
                ({selectedDebt?.type === 'outgoing' ? '應付餘額' : '應收餘額'})
              </span>
            </DialogTitle>
            <DialogDescription className="text-muted-foreground font-medium pt-1">
              {selectedDebt?.type === 'outgoing' ? '下方列出您需要支付給對方的支出明細。' : '下方列出對方需要支付給您的支出明細。'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto no-scrollbar space-y-6 my-4 pr-1">
            <div className={`p-6 rounded-2xl border border-border/40 flex items-center justify-between ${selectedDebt?.type === 'outgoing' ? 'bg-destructive/5' : 'bg-secondary/5'}`}>
              <div>
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest block mb-1">
                  {selectedDebt?.type === 'outgoing' ? '待支付總計' : '待入帳總計'}
                </span>
                <div className={`text-3xl font-black ${selectedDebt?.type === 'outgoing' ? 'text-destructive' : 'text-secondary'}`}>
                  ${formatAmount(selectedDebt?.user.totalAmount ?? 0)}
                </div>
              </div>
              <Badge className={`${selectedDebt?.type === 'outgoing' ? 'bg-destructive text-destructive-foreground' : 'bg-secondary text-secondary-foreground'} rounded-full px-4 h-8 font-bold`}>
                待核銷
              </Badge>
            </div>

            <div className="space-y-2">
              <h5 className="text-[10px] font-bold text-muted-foreground/95 uppercase tracking-widest ml-1 mb-2">
                支出細項（勾選要結清的項目）
              </h5>
              {selectedDebt?.user.details.map((detail, idx) => {
                const isSelected = selectedItemIds.includes(detail.participantId);
                const linkedExpense = expenses.find((exp: Expense) => exp.id === detail.expenseId);
                const isOutgoing = selectedDebt.type === 'outgoing';
                const rowSelected = isOutgoing
                  ? 'bg-red-950/50 border-red-500/55 shadow-[inset_0_0_0_1px_rgba(239,68,68,0.25)]'
                  : 'bg-emerald-950/45 border-emerald-500/50 shadow-[inset_0_0_0_1px_rgba(52,199,89,0.25)]';
                const rowIdle = 'bg-muted/70 border-zinc-600/55 hover:border-zinc-500/80 hover:bg-muted/90';
                return (
                  <CheckboxRow
                    key={`${detail.participantId}-${idx}`}
                    checked={isSelected}
                    onCheckedChange={() => handleItemToggle(detail.participantId)}
                    checkboxClassName={cn(
                      isSelected && isOutgoing && 'border-destructive bg-destructive text-destructive-foreground',
                      isSelected && !isOutgoing && 'border-secondary bg-secondary text-secondary-foreground',
                    )}
                    className={cn(
                      'p-3 sm:p-4 rounded-xl border-2 gap-3',
                      isSelected ? rowSelected : rowIdle,
                    )}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="w-8 h-8 rounded-lg overflow-hidden border-2 border-zinc-600/70 shrink-0">
                          <AvatarImage src={selectedDebt?.user.userId ? users.find((u: User) => u.id === selectedDebt.user.userId)?.avatarUrl : undefined} className="object-cover" />
                          <AvatarFallback
                            className={cn(
                              'text-xs font-bold',
                              isOutgoing ? 'bg-destructive/20 text-destructive' : 'bg-secondary/20 text-secondary',
                            )}
                          >
                            {selectedDebt?.user.nickname[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <h5 className="text-sm font-bold text-foreground line-clamp-1">{detail.title}</h5>
                          <span className="text-[10px] text-muted-foreground font-medium flex items-center gap-1">
                            <Calendar size={10} className="opacity-90" /> {new Date(detail.date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className={cn(
                            'font-black text-sm tabular-nums',
                            isOutgoing ? 'text-destructive' : 'text-secondary',
                          )}
                        >
                          ${formatAmount(detail.amount)}
                        </span>
                        {linkedExpense && (
                          <span
                            role="button"
                            tabIndex={0}
                            title="查看支出詳情"
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-black/20"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedExpenseDetail(linkedExpense);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedExpenseDetail(linkedExpense);
                              }
                            }}
                          >
                            <ChevronRight size={16} />
                          </span>
                        )}
                      </div>
                    </div>
                  </CheckboxRow>
                );
              })}
            </div>

            {/* Receipt Selection UI */}
            <div className="mt-4 pt-4 border-t border-border/20">
              <h5 className="text-xs font-bold text-muted-foreground uppercase tracking-widest ml-1 mb-3 flex items-center gap-2">
                <Camera size={12} /> 上傳還款收據 / 截圖 (選填)
              </h5>
              <div className={`relative border-2 border-dashed rounded-2xl min-h-[140px] flex items-center justify-center overflow-hidden transition-all ${receiptPreview ? 'border-primary' : 'border-border hover:border-primary/50 bg-background/50'}`}>
                {receiptPreview ? (
                  <div className="relative w-full h-full">
                    <img src={receiptPreview} alt="Receipt" className="w-full h-full object-cover max-h-[200px]" />
                    <button
                      onClick={(e) => { e.stopPropagation(); setReceiptFile(null as any); setReceiptPreview(''); }}
                      className="absolute top-2 right-2 p-2 bg-black/60 text-white rounded-full hover:bg-black/80 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <label className="inset-0 absolute cursor-pointer flex flex-col items-center justify-center p-4">
                    <Plus size={20} className="text-primary mb-2" />
                    <p className="text-[10px] font-bold text-foreground">點擊上傳付款證明</p>
                    <input type="file" accept="image/*" className="hidden" onChange={handleReceiptUpload} />
                  </label>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 flex flex-col gap-3 pt-4 border-t border-border/30">
            {selectedDebt?.type === 'incoming' && (
              <Button
                variant="outline"
                className="w-full h-12 rounded-xl font-bold border-secondary/30 text-secondary hover:bg-secondary/10 flex items-center gap-2"
                disabled={isReminding}
                onClick={handleSendReminder}
              >
                {isReminding ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
                傳送 Email 催帳提醒
              </Button>
            )}
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1 h-14 rounded-2xl font-bold border-border hover:bg-muted-foreground/10"
                onClick={() => { setSelectedDebt(null); popLayer(); }}
              >
                取消
              </Button>
              <Button
                className={`flex-1 h-14 rounded-2xl font-black text-lg shadow-sm flex items-center gap-2 ${selectedDebt?.type === 'outgoing' ? 'bg-destructive text-white hover:bg-destructive/90' : 'bg-secondary text-secondary-foreground hover:bg-secondary/90'}`}
                disabled={isSettling || (selectedItemIds.length === 0 && selectedDebt?.user.details.length !== 0)}
                onClick={handleSettle}
              >
                {isSettling ? <Loader2 size={24} className="animate-spin" /> : <CheckCircle2 size={24} />}
                {selectedDebt?.type === 'outgoing' ? '確認支付所選' : '確認收到款項'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Settlement Detail Modal */}
      <Dialog open={!!selectedSettlement} onOpenChange={(open) => !open && closeSettlementDialog()}>
        <DialogContent className="bg-muted border-border rounded-[2rem] p-0 max-w-[min(95vw,1100px)] shadow-wash max-h-[90dvh] flex flex-col overflow-hidden">
          <div className="overflow-y-auto no-scrollbar flex-1 p-4 sm:p-6 md:p-8">
          <DialogHeader>
            <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center mb-4 ${selectedSettlement?.fromUserId === currentUser?.id ? 'bg-destructive/10 text-destructive' : 'bg-secondary/10 text-secondary'}`}>
              <ReceiptText size={28} />
            </div>
            <DialogTitle className="text-xl sm:text-2xl font-black text-primary">結算細節</DialogTitle>
            <DialogDescription className="text-muted-foreground font-medium pt-1">
              下方列出本次結算的細節
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 sm:space-y-6 my-4 sm:my-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              <div className="bg-background/50 p-4 rounded-2xl border border-border/50">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest block mb-1">付款人</span>
                <div className="text-sm font-bold">{users.find((u: User) => u.id === selectedSettlement?.fromUserId)?.nickname}</div>
              </div>
              <div className="bg-background/50 p-4 rounded-2xl border border-border/50">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest block mb-1">收款人</span>
                <div className="text-sm font-bold">{users.find((u: User) => u.id === selectedSettlement?.toUserId)?.nickname}</div>
              </div>
            </div>

            <div className="bg-background/50 p-4 sm:p-6 rounded-2xl border border-border/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest block mb-1">結算金額</span>
                <div className="text-3xl font-black text-primary">${selectedSettlement?.totalAmount.toLocaleString()}</div>
              </div>
              <div className="text-right">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest block mb-1">結算時間</span>
                <div className="text-xs font-medium flex items-center gap-1">
                  <Clock size={12} /> {selectedSettlement && new Date(selectedSettlement.createdAt).toLocaleString()}
                </div>
              </div>
            </div>

            {settlementLinkedExpenses.length > 0 && (
              <div className="space-y-3">
                <h5 className="text-xs font-bold text-muted-foreground uppercase tracking-widest ml-1 flex items-center gap-2">
                  <ReceiptText size={12} /> 結算項目與細項清單
                </h5>
                <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                  {settlementLinkedExpenses.map(({ expense, linkedParticipants }) => (
                    <div key={expense.id} className="p-4 rounded-xl border border-border/30 bg-background/40">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-sm">{expense.title}</span>
                        <span className="text-xs font-black text-primary">
                          ${linkedParticipants.reduce((sum: number, p: any) => sum + p.amountDue, 0).toLocaleString()}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {(expense.items && expense.items.length > 0 ? expense.items : [{ id: `${expense.id}-default`, name: '無明細資料', amount: expense.amount }]).map((item: any, index: number) => (
                          <div key={`${item.id}-${index}`} className="text-xs text-muted-foreground flex items-center justify-between">
                            <span>{item.name || `細項 ${index + 1}`}</span>
                            <span>${item.amount.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedSettlement?.receiptUrl && (
              <div className="space-y-3">
                <h5 className="text-xs font-bold text-muted-foreground uppercase tracking-widest ml-1 flex items-center gap-2">
                  <Camera size={12} /> 付款證明
                </h5>
                <div
                  className="relative rounded-2xl overflow-hidden cursor-zoom-in group border-2 border-border/40 hover:border-primary/50 transition-colors"
                  onClick={() => { setZoomedImage(selectedSettlement.receiptUrl!); pushLayer('zoomedImage'); }}
                >
                  <img src={selectedSettlement.receiptUrl} alt="Receipt" className="w-full h-auto max-h-[240px] object-contain bg-black/20" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="p-3 bg-white/20 backdrop-blur-md rounded-full text-white">
                      <Plus size={24} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          </div>{/* end scroll */}
          <div className="shrink-0 px-4 sm:px-6 md:px-8 pb-4 sm:pb-6 md:pb-8 pt-3 border-t border-border/20 space-y-3">
          <button
            onClick={handleShare}
            className="w-full flex items-center justify-center gap-2 h-10 rounded-2xl font-black text-xs border border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
          >
            {copied ? <Check size={15} className="text-secondary" /> : <Copy size={15} />}
            {copied ? '已複製連結' : '分享此頁面'}
          </button>
          <Button
            className="w-full h-12 sm:h-14 rounded-2xl font-black text-base sm:text-lg shadow-md"
            onClick={() => closeSettlementDialog()}
          >
            關閉視窗
          </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      {zoomedImage && (
        <ImageLightbox src={zoomedImage} onClose={() => { setZoomedImage(null); popLayer(); }} />
      )}
      <Dialog open={!!selectedExpenseDetail} onOpenChange={(open) => !open && setSelectedExpenseDetail(null)}>
        <DialogContent className="bg-muted border-border rounded-[2.5rem] p-0 max-w-2xl shadow-wash max-h-[90dvh] flex flex-col overflow-hidden">
          <div className="overflow-y-auto no-scrollbar flex-1 p-8">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-primary">{selectedExpenseDetail?.title}</DialogTitle>
            <DialogDescription>支出紀錄詳細內容</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 my-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-background/40 rounded-xl p-3 border border-border/30 text-xs">
                金額：<span className="font-black text-sm">${selectedExpenseDetail?.amount.toLocaleString()}</span>
              </div>
              <div className="bg-background/40 rounded-xl p-3 border border-border/30 text-xs">
                日期：<span className="font-bold">{selectedExpenseDetail && new Date(selectedExpenseDetail.expenseDate).toLocaleDateString()}</span>
              </div>
            </div>
            <div>
              <h5 className="text-xs font-bold mb-2">明細清單</h5>
              <div className="space-y-2 max-h-52 overflow-y-auto">
                {(selectedExpenseDetail?.items || []).map((item: any, idx: number) => (
                  <div key={`${item.id}-${idx}`} className="flex items-center justify-between text-xs bg-background/40 border border-border/20 rounded-lg px-3 py-2">
                    <span>{item.name || `項目 ${idx + 1}`}</span>
                    <span className="font-black">${item.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>{/* end scroll */}
          <div className="shrink-0 px-8 pb-8 pt-3 border-t border-border/20">
          <Button className="w-full" onClick={() => setSelectedExpenseDetail(null)}>關閉</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
